import React, { useCallback } from 'react';
import {
    Tab,
    TabList,
    TabListProps,
    useOverflowMenu,
    Menu,
    MenuTrigger,
    Button,
    MenuPopover,
    MenuList,
    MenuItem,
    useIsOverflowItemVisible,
    Overflow,
    OverflowItem,
    mergeClasses,
} from '@fluentui/react-components';
import {
    bundleIcon,
    MoreHorizontalFilled,
    MoreHorizontalRegular,
} from '@fluentui/react-icons';
import { useResponsiveTabListStyles } from './ResponsiveTabList.styles';

const MoreHorizontal = bundleIcon(MoreHorizontalFilled, MoreHorizontalRegular);

export interface ResponsiveTabItem<T extends string = string> {
    id: T;
    label: string;
}

type ItemSelectCallback = (id: string) => void;

export interface ResponsiveTabListProps {
    readonly items: ResponsiveTabItem[];
    selectedId: string;
    onTabSelect: ItemSelectCallback;
    tabListProps?: TabListProps;
    className?: string;
}

const OverflowMenuItem: React.FC<{
    item: ResponsiveTabItem;
    onClick: () => void;
}> = ({ item, onClick }) => {
    const isVisible = useIsOverflowItemVisible(item.id);

    return !isVisible ? (
        <MenuItem key={item.id} onClick={onClick}>
            <div>{item.label}</div>
        </MenuItem>
    ) : null;
};

const OverflowMenu: React.FC<{
    readonly items: ResponsiveTabItem[];
    onItemClick: ItemSelectCallback;
}> = ({ items, onItemClick }) => {
    const { ref, isOverflowing, overflowCount } =
        useOverflowMenu<HTMLButtonElement>();

    return isOverflowing ? (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Button
                    appearance="transparent"
                    ref={ref}
                    icon={<MoreHorizontal />}
                    aria-label={`${overflowCount} more tabs`}
                    role="tab"
                />
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {items.map((item) => (
                        <OverflowMenuItem
                            key={item.id}
                            item={item}
                            onClick={() => onItemClick(item.id)}
                        />
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    ) : null;
};

const ResponsiveTabList: React.FC<ResponsiveTabListProps> = ({
    items,
    selectedId,
    onTabSelect,
    tabListProps,
    className,
}) => {
    const styles = useResponsiveTabListStyles();

    const handleTabSelect: TabListProps['onTabSelect'] = useCallback(
        (_ev, data) => {
            if (data.value) {
                onTabSelect(data.value as string);
            }
        },
        [onTabSelect]
    );

    return (
        <div className={mergeClasses(styles.root, className)}>
            <Overflow minimumVisible={1}>
                <TabList
                    {...tabListProps}
                    selectedValue={selectedId}
                    onTabSelect={handleTabSelect}
                >
                    {items.map((item) => (
                        <OverflowItem
                            key={item.id}
                            id={item.id}
                            priority={item.id === selectedId ? 2 : 1}
                        >
                            <Tab value={item.id}>{item.label}</Tab>
                        </OverflowItem>
                    ))}
                    <OverflowMenu items={items} onItemClick={onTabSelect} />
                </TabList>
            </Overflow>
        </div>
    );
};

export default ResponsiveTabList;
